import { ActionIcon, Badge, Button, CopyButton, Group, Menu, Stack, Table, Text, Tooltip } from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconCopy, IconDotsVertical, IconPlus } from '@tabler/icons-react'
import type { SsoApplication } from '@/api/sso'
import { ssoStatusMeta } from './ssoStatus'

interface Props {
  apps: SsoApplication[]
  provedorAtivo: boolean
  podeGerenciar: boolean
  onNova: () => void
  onEditar: (a: SsoApplication) => void
  onProvisionar: (a: SsoApplication) => void
  onStatus: (a: SsoApplication, status: 'paused' | 'active') => void
}

const MONO = { fontFamily: 'var(--mantine-font-family-monospace)' }
// O caminho do segredo repete realm e prefixo em toda linha; a coluna mostra só o
// último trecho, e o botão copia o nome inteiro.
const nomeCurto = (ref: string) => ref.slice(ref.lastIndexOf('/') + 1)

export function AplicacoesTable({ apps, provedorAtivo, podeGerenciar, onNova, onEditar, onProvisionar, onStatus }: Props) {
  return (
    <>
      {podeGerenciar && (
        <Group gap="sm" mb="sm" justify="flex-start">
          <Button size="xs" leftSection={<IconPlus size={16} />} onClick={onNova} disabled={!provedorAtivo}>
            Nova aplicação
          </Button>
          {!provedorAtivo && <Text size="xs" c="dimmed">configure o provedor primeiro</Text>}
        </Group>
      )}
      {apps.length === 0 ? (
        <Text size="sm" c="dimmed">Nenhuma aplicação.</Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Cliente</Table.Th>
                <Table.Th>Nome</Table.Th>
                <Table.Th>ID/ENV</Table.Th>
                <Table.Th>Redirects</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Segredo</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {apps.map((a) => {
                const meta = ssoStatusMeta(a.status)
                const erro = a.config.last_error
                return (
                  <Table.Tr key={a.id}>
                    <Table.Td>{a.tenant_name ?? `tenant ${a.tenant_id}`}</Table.Td>
                    <Table.Td>{a.name}</Table.Td>
                    <Table.Td>
                      <Text size="sm" style={MONO}>{a.keycloak_client_id}</Text>
                      {a.config.default_role && (
                        <Text size="xs" c="dimmed">papel padrão: <span style={MONO}>{a.config.default_role}</span></Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {(a.config.redirect_uris ?? []).length > 0 ? (
                        <Group gap={6} wrap="nowrap">
                          <Text size="xs" style={MONO}>{(a.config.redirect_uris ?? [])[0]}</Text>
                          {(a.config.redirect_uris ?? []).length > 1 && (
                            <Tooltip
                              multiline maw={360}
                              label={<Stack gap={2}>{(a.config.redirect_uris ?? []).slice(1).map((u) => (
                                <Text key={u} size="xs" style={MONO}>{u}</Text>
                              ))}</Stack>}
                            >
                              <Badge variant="light" color="gray" tabIndex={0}>{`+${(a.config.redirect_uris ?? []).length - 1}`}</Badge>
                            </Tooltip>
                          )}
                        </Group>
                      ) : <Text size="xs" c="dimmed">-</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Badge color={meta.color} variant="light">{meta.label}</Badge>
                        {erro && (
                          <Tooltip label={erro} multiline maw={320}>
                            <span tabIndex={0} role="img" aria-label="Erro de provisionamento" style={{ display: 'inline-flex' }}>
                              <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />
                            </span>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {a.secret_ref ? (
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label={a.secret_ref} multiline maw={420}>
                            <Text size="xs" style={MONO} tabIndex={0}>{nomeCurto(a.secret_ref)}</Text>
                          </Tooltip>
                          <CopyButton value={a.secret_ref}>
                            {({ copied, copy }) => (
                              <ActionIcon variant="subtle" size="sm" aria-label="Copiar nome do segredo" onClick={copy}>
                                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                              </ActionIcon>
                            )}
                          </CopyButton>
                        </Group>
                      ) : <Text size="xs" c="dimmed">-</Text>}
                    </Table.Td>
                    <Table.Td>
                      {podeGerenciar && (
                        <Menu position="bottom-end" transitionProps={{ duration: 0 }}>
                          <Menu.Target>
                            <ActionIcon variant="subtle" aria-label="Ações da aplicação"><IconDotsVertical size={16} /></ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => onEditar(a)}>Editar redirects</Menu.Item>
                            {a.status === 'draft' && <Menu.Item onClick={() => onProvisionar(a)}>Provisionar</Menu.Item>}
                            {(a.status === 'error' || (a.status === 'active' && a.config.last_error)) && (
                              <Menu.Item onClick={() => onProvisionar(a)}>Reprovisionar</Menu.Item>
                            )}
                            {a.status === 'active' && <Menu.Item onClick={() => onStatus(a, 'paused')}>Pausar</Menu.Item>}
                            {a.status === 'paused' && <Menu.Item onClick={() => onStatus(a, 'active')}>Reativar</Menu.Item>}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </>
  )
}
